import { useMemo, useState } from 'react';
import { Card } from '../../layout/Card.jsx';
import { Icon } from '../../layout/Icon.jsx';
import { useAuth } from '../../shared/useAuth.js';
import { GateCard } from './components/GateCard.jsx';
import { EventFeed } from './components/EventFeed.jsx';
import { SimulateScanModal } from './components/SimulateScanModal.jsx';
import { ManualOverrideModal } from './components/ManualOverrideModal.jsx';
import { useGates } from './hooks/useGates.js';
import { useGateEvents } from './hooks/useGateEvents.js';

const PLACEHOLDER_GATES = [
  { _id: 'p1', name: 'بوابة الدخول 1', direction: 'in', status: 'offline', _placeholder: true },
  { _id: 'p2', name: 'بوابة الدخول 2', direction: 'in', status: 'offline', _placeholder: true },
  { _id: 'p3', name: 'بوابة الخروج 1', direction: 'out', status: 'offline', _placeholder: true },
  { _id: 'p4', name: 'بوابة الخروج 2', direction: 'out', status: 'offline', _placeholder: true }
];

export default function GateMonitor() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canSimulate = hasPermission('gates.simulate');
  const canOverride = hasPermission('gates.override');

  const { data: gates = [], isLoading: gatesLoading } = useGates();
  const { events, loading: eventsLoading } = useGateEvents();

  const [simOpen, setSimOpen] = useState(false);
  const [overrideGate, setOverrideGate] = useState(null);

  const displayGates = gates.length > 0 ? gates : PLACEHOLDER_GATES;

  // last event per gate
  const lastByGate = useMemo(() => {
    const map = new Map();
    for (const e of events) {
      const gid = e.gateId || e.gate?._id || e.gate?.id;
      if (gid && !map.has(gid)) map.set(gid, e);
    }
    return map;
  }, [events]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-[10px] bg-white border border-line">
          <span className="live-dot" />
          <span className="text-[12.5px] font-semibold">متصل</span>
          <span className="text-[11px] text-ink-3">{gates.length} أجهزة</span>
        </div>
        {canSimulate ? (
          <button type="button" className="btn btn-primary" onClick={() => setSimOpen(true)}>
            <Icon name="scan" size={14} /> محاكاة قراءة
          </button>
        ) : null}
      </div>

      {gatesLoading ? (
        <Card>
          <div className="text-center py-6 text-ink-3">جاري تحميل البوابات...</div>
        </Card>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {displayGates.map((g) => (
            <GateCard
              key={g._id || g.id}
              gate={g}
              lastEvent={lastByGate.get(g._id || g.id)}
              canOverride={canOverride && !g._placeholder}
              onOverride={setOverrideGate}
            />
          ))}
        </div>
      )}

      <EventFeed events={events} loading={eventsLoading} />

      <SimulateScanModal
        open={simOpen}
        gates={gates}
        onClose={() => setSimOpen(false)}
      />
      <ManualOverrideModal
        open={!!overrideGate}
        gate={overrideGate}
        onClose={() => setOverrideGate(null)}
      />
    </div>
  );
}
